import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Layout, Spin, Button, Input, Modal, message, Tooltip, Rate } from 'antd';
import {
  CheckOutlined, MessageOutlined, SendOutlined,
  LeftOutlined, RightOutlined, FilePdfOutlined,
  SaveOutlined, CheckCircleOutlined, CloseOutlined,
  UndoOutlined, EditOutlined, StopOutlined, ExclamationCircleOutlined, InfoCircleOutlined,
} from '@ant-design/icons';
import AgentConfigService from '../services/AgentConfigService';
import HCCAddCodeModal from './reviewPageComponents/HCCAddCodeModal';
import FinalizeReviewModal from './reviewPageComponents/FinalizeReviewModal';
import RuleDerivationPanel from './reviewPageComponents/RuleDerivationPanel';

import ReactMarkdown from 'react-markdown';

// ── Split-file imports ───────────────────────────────────────────────────────
import {
  C, W, REVIEW_STATUS, renderOcrWithHighlights,
  HccBadge, GOSPEL_SECTIONS, ReviewStatusDot, SupportLabel,
} from './reviewPageComponents/HCCReviewConstants';
import {
  EncounterList, CodeList, CodesAcceptedPopup,
  DecisionNoteModal, DecisionHistoryModal,
  ChatPopup, P3PdfViewer, EvidenceNavigator, PageStrip,
} from './reviewPageComponents/HCCPanelComponents';
import { useAudit, AuditResultsPopup } from './reviewPageComponents/HCCAudit';
import { useDecisions } from './reviewPageComponents/HCCDecisions';
import { useTelemetry } from './reviewPageComponents/HCCTelemetry';
import CodingGuidelinesButton from './reviewPageComponents/CodingGuidelinesButton';



const { Header } = Layout;

// ─────────────────────────────────────────────────────────────────────────────
// Main: HCCReviewPage
// ─────────────────────────────────────────────────────────────────────────────
const HCCReviewPage = ({ username, sessionId, userRole, onLogout }) => {

  const { chartId }  = useParams();
  const navigate     = useNavigate();
  const [searchParams] = useSearchParams();
  const batchId      = searchParams.get('batch_id');

  // ── Data state ──────────────────────────────────────────────────────────────
  const [encounters,       setEncounters]       = useState([]);
  const [encountersLoading,setEncountersLoading]= useState(true);
  const [selectedEnc,      setSelectedEnc]      = useState(null);
  const [encounterTrace,   setEncounterTrace]   = useState(null);
  const [traceLoading,     setTraceLoading]     = useState(false);

  // ── Navigation state ────────────────────────────────────────────────────────
  const [selectedFinding,  setSelectedFinding]  = useState(null);
  const [activePdfPage,    setActivePdfPage]    = useState(null);
  const [activeEvidenceIdx,    setActiveEvidenceIdx]    = useState(0);
  const [activeCodeEvidenceIdx, setActiveCodeEvidenceIdx] = useState(0);

  // ── Work queue base URL ─────────────────────────────────────────────────────
  const [wqBaseUrl,        setWqBaseUrl]        = useState('');
  const selectedFindingRef = useRef(null);

  // ── OCR state ───────────────────────────────────────────────────────────────
  const [ocrText,          setOcrText]          = useState('');
  const [ocrLoading,       setOcrLoading]       = useState(false);

  // ── Floating panel + chat ───────────────────────────────────────────────────
  const [floatingPanel,    setFloatingPanel]    = useState(null);
  const [chatMessages,     setChatMessages]     = useState([]);
  const [chatInput,        setChatInput]        = useState('');
  const [chatLoading,      setChatLoading]      = useState(false);

  // ── PDF modal ───────────────────────────────────────────────────────────────
  const [modalPdfUrl,      setModalPdfUrl]      = useState(null);
  const [modalPdfLoading,  setModalPdfLoading]  = useState(false);
  const [modalVisible,     setModalVisible]     = useState(false);
  const [modalPage,        setModalPage]        = useState(null);
  const modalPdfRef        = useRef(null);

  // ── Modals ──────────────────────────────────────────────────────────────────
  const [addCodeModalVisible, setHCCAddCodeModalVisible] = useState(false);
  const [finalizeModalVisible, setFinalizeModalVisible] = useState(false);
  const [viewOnly, setViewOnly] = useState(false);

  // ── Computed values ─────────────────────────────────────────────────────────
  const findings = encounterTrace?.coded_findings || [];
  selectedFindingRef.current = selectedFinding;

  // Active evidence item for P3 highlight + strip — recalculates when finding or idx changes
  const activeCodeEvidenceItem = useMemo(() => {
    const ev = selectedFinding?.evidence?.[activeCodeEvidenceIdx];
    if (!ev) return null;
    return {
      matchText:    ev.context?.match || ev.entity_text || '',
      absolutePage: ev.page,
      section:      ev.section,
    };
  }, [selectedFinding, activeCodeEvidenceIdx]);
  // P3: pages where the code entity was found (drives page strip highlights + PDF)
  const codeEvidenceItems = selectedFinding?.evidence || [];
  const evidencePages     = [...new Set(codeEvidenceItems.map(e => e.page))];
  // P4: corroboration supporting evidence (labs, meds) — what SUPPORTS the code
  const supportItems      = selectedFinding?.corroboration?.supporting_evidence || [];
  const currentSupport    = supportItems[activeEvidenceIdx];

  // ── Service base URL helper ─────────────────────────────────────────────────
  const getBaseUrl = useCallback(
    () => AgentConfigService.getWorkQueueServiceUrl(),
    []
  );

  // ── Load OCR text ───────────────────────────────────────────────────────────
  const loadOcr = useCallback(async (pageNum) => {
    if (!pageNum || !chartId) return;
    setOcrLoading(true);
    setOcrText('');
    try {
      const base = await getBaseUrl();
      const resp = await fetch(`${base}/hcc/charts/${chartId}/pages/${pageNum}/ocr`, {
        headers: { Authorization: `Bearer ${sessionId}` }, credentials: 'include',
      });
      if (!resp.ok) throw new Error(`${resp.status}`);
      setOcrText(await resp.text());
    } catch (err) {
      setOcrText(`[OCR not available for page ${pageNum}]`);
    } finally {
      setOcrLoading(false);
    }
  }, [chartId, sessionId, getBaseUrl]);

  // ── Hooks from split files ──────────────────────────────────────────────────

  const {
    auditAvailable, auditSuspects, auditPhase, auditProgress,
    auditFindings, auditMeta, auditPopupVisible, auditConfirmVisible,
    setAuditPopupVisible, setAuditConfirmVisible,
    handleAuditClick, handleAuditConfirm, handleAuditRerun,
  } = useAudit({ encounterTrace, selectedEnc, chartId, sessionId });

  const {
    decisions, setDecisions, acceptedFindings, setAcceptedFindings,
    historyCounts, setHistoryCounts,
    decisionModal, historyModal,
    setDecisionModal, setHistoryModal,
    persistDecision, setDecisionFor,
    handleAccept, handleModify, handleReject, handleEscalate,
    handleDecisionModalConfirm,
    handleRevertDecision, handleRevert,
    fetchDecisionHistory,
  } = useDecisions({
    chartId, sessionId, selectedEnc, getBaseUrl, findings,
    encounterTrace, setEncounterTrace,
    selectedFinding, setSelectedFinding,
  });


  const { telemetryRootRef } = useTelemetry({
    chartId,
    sessionId,
    encounterId: selectedEnc?.encounter_id,
    activePdfPage,
  });  


  // ── Load work queue base URL once ────────────────────────────────────────────

  useEffect(() => {
    getBaseUrl().then(url => setWqBaseUrl(url)).catch(() => {});
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!chartId) return;
    getBaseUrl().then(base =>
      fetch(`${base}/work-items/${chartId}/status`, {
        headers: { Authorization: `Bearer ${sessionId}` },
        credentials: 'include',
      })
    )
    .then(r => r.ok ? r.json() : null)
    .then(data => { if (data?.view_only) setViewOnly(true); })
    .catch(() => {});
  }, [chartId, sessionId, getBaseUrl]);


  // ── Encounter PDF params — memoized, stable per encounter ─────────────────
  const encounterPdfParams = useMemo(() => {
    if (!wqBaseUrl || !selectedEnc?.encounter_id) return null;
    return {
      url: `${wqBaseUrl}/hcc/charts/${chartId}/encounters/${selectedEnc.encounter_id}/pdf`,
      httpHeaders: { Authorization: `Bearer ${sessionId}` },
    };
  }, [wqBaseUrl, chartId, selectedEnc?.encounter_id, sessionId]);


  // ── Load encounters on mount ────────────────────────────────────────────────
  useEffect(() => {
    if (!chartId) return;
    setEncountersLoading(true);
    getBaseUrl().then(base =>
      fetch(`${base}/hcc/charts/${chartId}/encounters`, {
        headers: { Authorization: `Bearer ${sessionId}` }, credentials: 'include',
      })
    ).then(r => r.ok ? r.json() : Promise.reject(r.status))
     .then(data => {
       setEncounters(data.encounters || []);
       // Auto-select first encounter with codes
       const first = (data.encounters || []).find(e => e.coded_findings_count > 0)
                  || (data.encounters || [])[0];
       if (first) handleSelectEncounter(first);
     })
     .catch(err => { console.error('Encounters load error:', err); message.error('Failed to load chart encounters'); })
     .finally(() => setEncountersLoading(false));
  }, [chartId, sessionId]); // eslint-disable-line

  // Cleanup blob URLs on unmount

  useEffect(() => () => {
    if (modalPdfRef.current)  URL.revokeObjectURL(modalPdfRef.current);
  }, []);


  const handleSelectEncounter = useCallback(async (enc) => {
    setSelectedEnc(enc);
    setSelectedFinding(null);
    setEncounterTrace(null);
    setActiveEvidenceIdx(0);
    setOcrText('');
    setActivePdfPage(enc.start_page);
    setTraceLoading(true);
    try {
      const base = await getBaseUrl();
      const resp = await fetch(
        `${base}/hcc/charts/${chartId}/encounters/${enc.encounter_id}/trace`,
        { headers: { Authorization: `Bearer ${sessionId}` }, credentials: 'include' }
      );
      if (!resp.ok) throw new Error(`${resp.status}`);
      const trace = await resp.json();
      setEncounterTrace(trace);
      // Auto-select first finding if available
      const firstFinding = trace.coded_findings?.[0];
      if (firstFinding) handleSelectFinding(firstFinding, enc);
      // else: P3PdfViewer shows encounter from start_page by default
    } catch (err) {
      console.error('Trace load error:', err);
      message.error('Failed to load encounter codes');
    } finally {
      setTraceLoading(false);
    }
  }, [chartId, sessionId, getBaseUrl]); // eslint-disable-line


  // ── Select finding ──────────────────────────────────────────────────────────
  const handleSelectFinding = useCallback((finding, enc) => {
    setSelectedFinding(finding);
    setActiveEvidenceIdx(0);
    setActiveCodeEvidenceIdx(0);
    setOcrText('');
    // P3: show the page where the code entity appears
    const firstCodeEvidence = finding.evidence?.[0];
    const pdfPage = firstCodeEvidence?.page || (enc || selectedEnc)?.start_page;
    if (pdfPage) setActivePdfPage(pdfPage);
    // P4: show the page where corroboration support appears
    const firstSupport = finding.corroboration?.supporting_evidence?.[0];
    if (firstSupport?.page) {
      loadOcr(firstSupport.page);
    }
  }, [selectedEnc, loadOcr]);

  // ── P4 support navigator — navigates corroboration items, P3 stays fixed ────
  const handleSupportNav = useCallback((idx) => {
    if (!supportItems[idx]) return;
    setActiveEvidenceIdx(idx);
    // P4 only: load the support item's page OCR — P3 PDF stays on code evidence page
    if (supportItems[idx].page) loadOcr(supportItems[idx].page);
  }, [supportItems, loadOcr]);

  // Load OCR when support idx changes
  useEffect(() => {
    if (currentSupport?.page) loadOcr(currentSupport.page);
  }, [activeEvidenceIdx]); // eslint-disable-line

  // ── P2 evidence item click — scroll P3 + update evidence strip ─────────────
  const handleCodeEvidenceClick = useCallback((idx) => {
    if (!selectedFinding?.evidence?.[idx]) return;
    setActiveCodeEvidenceIdx(idx);
    setActivePdfPage(selectedFinding.evidence[idx].page);
  }, [selectedFinding]);


  // ── Add Support from P3 selection ─────────────────────────────────────────
  // Called by P3SelectionTip when coder highlights text in the PDF.
  // Uses selectedFindingRef (not state) so this callback is always stable.
  const handleAddSupportFromP3 = useCallback((selection) => {
    const finding = selectedFindingRef.current;
    if (!finding || !selection?.text) return;

    const newSupportItem = {
      evidence_id:    `coder-sup-p3-${Date.now()}`,
      evidence_type:  'coder_added',
      text:           selection.text,
      page:           selection.absolutePage,
      char_span:      null,
      context:        { before: '', match: selection.text, after: '' },
      section:        'coder_added',
    };

    const currentSupportLen = finding.corroboration?.supporting_evidence?.length ?? 0;

    // Update encounterTrace (source of truth for P2 status dots)
    setEncounterTrace(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        coded_findings: prev.coded_findings.map(f => {
          if (f.finding_id !== finding.finding_id) return f;
          return {
            ...f,
            support_status:  'supported',
            display_status:  'direct',
            corroboration: {
              ...f.corroboration,
              status: 'supported',
              supporting_evidence: [
                ...(f.corroboration?.supporting_evidence || []),
                newSupportItem,
              ],
              llm_reasoning: f.corroboration?.llm_reasoning || 'Support added by coder',
            },
          };
        }),
      };
    });

    // Update selectedFinding so P4 re-renders immediately
    setSelectedFinding(prev => {
      if (!prev || prev.finding_id !== finding.finding_id) return prev;
      return {
        ...prev,
        support_status:  'supported',
        display_status:  'direct',
        corroboration: {
          ...prev.corroboration,
          status: 'supported',
          supporting_evidence: [
            ...(prev.corroboration?.supporting_evidence || []),
            newSupportItem,
          ],
        },
      };
    });

    // Navigate P4 to the new support item and load its OCR
    setActiveEvidenceIdx(currentSupportLen); // index of the newly added item
    if (selection.absolutePage) loadOcr(selection.absolutePage);
  }, [loadOcr]); // selectedFindingRef is a ref — not a dep

  // ── Handle new coder-added code ────────────────────────────────────────────
  
  // Called by AddCodeModal on confirm. Appends to encounterTrace + auto-accepts.
  const handleCodeAdded = useCallback((newFinding) => {
      // 1. Append to the current encounter's coded_findings (shows in P2)
      setEncounterTrace(prev => ({
        ...prev,
        coded_findings: [...(prev?.coded_findings || []), newFinding],
      }));

      // 2. Auto-accept (shows in accepted codes popup immediately)
      if (selectedEnc) {
        setAcceptedFindings(prev => {
          if (prev.some(f => f.findingId === newFinding.finding_id)) return prev;
          return [...prev, {
            findingId:        newFinding.finding_id,
            icd10_code:       newFinding.icd10_code,
            description:      newFinding.description,
            hcc_mapping:      newFinding.hcc_mapping,
            encounter_id:     selectedEnc.encounter_id,
            encounter_number: selectedEnc.encounter_number,
            encounter_date:   selectedEnc.encounter_date || selectedEnc.dos,
          }];
        });
      }

      // 3. Persist to DB as accepted, manually_added code
      persistDecision(newFinding, 'accepted');

      // 3b. Update decisions map
      setDecisions(prev => ({ ...prev, [newFinding.finding_id]: { decision: 'accepted', note: null } }));

      // 4. Auto-select the new finding so the coder can immediately see it in P3/P4
      setSelectedFinding(newFinding);
      if (newFinding.evidence?.[0]?.page) {
        setActivePdfPage(newFinding.evidence[0].page);
      }
    }, [selectedEnc, persistDecision]
  );


  const togglePanel = (panel) => setFloatingPanel(p => p === panel ? null : panel);

  const handleChatSend = async () => {
    if (!chatInput.trim() || chatLoading) return;

    // Tag message with current context so the chat shows what was active
    const userMsg = {
      role: 'user',
      content: chatInput.trim(),
      context: {
        encounter_id:     selectedEnc?.encounter_id || null,
        encounter_number: selectedEnc?.encounter_number ?? null,
        icd10_code:       selectedFinding?.icd10_code || null,
        finding_id:       selectedFinding?.finding_id || null,
      },
    };
    const newMessages = [...chatMessages, userMsg];
    setChatMessages(newMessages);
    setChatInput('');
    setChatLoading(true);
    try {
      const base = await getBaseUrl();
      const resp = await fetch(`${base}/hcc/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${sessionId}` },
        credentials: 'include',
        body: JSON.stringify({
          messages:     newMessages,
          chart_id:     chartId,
          batch_id:     batchId,
          encounter_id: selectedEnc?.encounter_id,
          finding_id:   selectedFinding?.finding_id,
          icd10_code:   selectedFinding?.icd10_code,
          max_tokens:   1000,
        }),
      });
      if (!resp.ok) throw new Error(`${resp.status}`);
      const data = await resp.json();
      // Backend returns full updated messages array with assistant response appended

      const assistantText = data.content?.find(b => b.type === 'text')?.text || 'No response.';
      setChatMessages(prev => [...prev, { role: 'assistant', content: assistantText }]);

      //setChatMessages(data.messages || []);

    } catch (err) {
      message.error('Chat request failed');
    } finally {
      setChatLoading(false);
    }
  };

  // ── View PDF modal — with page navigation ─────────────────────────────────
  const handleViewPdf = async (pageNum) => {
    if (!pageNum) return;
    setModalPage(pageNum);
    setModalPdfLoading(true);
    setModalVisible(true);
    if (modalPdfRef.current) { URL.revokeObjectURL(modalPdfRef.current); modalPdfRef.current = null; }
    try {
      const base = await getBaseUrl();
      const resp = await fetch(`${base}/hcc/charts/${chartId}/pages/${pageNum}/pdf`, {
        headers: { Authorization: `Bearer ${sessionId}` }, credentials: 'include',
      });
      if (!resp.ok) throw new Error(`${resp.status}`);
      const blob = await resp.blob();
      const url  = URL.createObjectURL(blob);
      modalPdfRef.current = url;
      setModalPdfUrl(url);
    } catch (err) {
      message.error('Could not load PDF page');
      setModalVisible(false);
    } finally {
      setModalPdfLoading(false);
    }
  };

  // ── Finalize modal open ──────────────────────────────────
  const handleFinalize = () => {
    setFinalizeModalVisible(true);
  };

  // ── Save Draft — persists work without changing status ────────────────────
  const handleSaveDraft = useCallback(async () => {
    try {
      const base = await getBaseUrl();
      // TODO: will persist collected codes once the table is built
      message.success('Draft saved');
    } catch (err) {
      console.error('Save draft error:', err);
      message.error('Failed to save draft');
    }
  }, [getBaseUrl, sessionId, chartId]);


  
  // ── Complete — mark chart as coded ────────────────────────────────────────
  const handleComplete = useCallback(async () => {
    try {
      const base = await getBaseUrl();
      const resp = await fetch(`${base}/work-items/${chartId}/complete`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${sessionId}` },
        credentials: 'include',
      });
      if (!resp.ok) throw new Error(`${resp.status}`);
      message.success('Chart marked as complete');
      navigate('/my-inventory/charts');
    } catch (err) {
      console.error('Complete error:', err);
      message.error('Failed to mark chart complete');
    }
  }, [getBaseUrl, sessionId, chartId, navigate]);



  // ── Render ──────────────────────────────────────────────────────────────────
  const panelHeadingStyle = {
    padding: '8px 12px',
    fontSize: 14, fontWeight: 700, letterSpacing: '0.1em',
    textTransform: 'uppercase', color: C.textMuted,
    borderBottom: `1px solid ${C.border}`,
    background: C.white, flexShrink: 0,
  };

  // P4 evidence panel: determine what to show
  // Support is only relevant when the selected evidence comes from a non-gospel section.
  // Gospel sections (A&P, HPI, discharge summary, physical exam) are self-sufficient.
  const activeEvSection = selectedFinding?.evidence?.[activeCodeEvidenceIdx]?.section;
  const isGospelEvidence = activeEvSection && GOSPEL_SECTIONS.has(activeEvSection.toLowerCase());
  const showSupport = supportItems.length > 0 && !isGospelEvidence;

  // Build highlight spans by searching the OCR text for the known match string.
  // char_span offsets are relative to a different text source (full chart or section
  // chunk), so we can't use them directly against per-page OCR.
  // Priority: context.match → entity_text → char_span as last resort.
  const getHighlightSpans = (text, ev) => {
    if (!text || !ev) return [];

    // Build a regex from a string that allows any whitespace (including newlines)
    // between words. OCR text frequently has newlines where context.match has spaces.
    const makeFlexRegex = (str) => {
      const s = str.trim();
      if (!s) return null;
      try {
        const escaped  = s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const flexible = escaped.replace(/\s+/g, '\\s+');
        return new RegExp(flexible, 'i');
      } catch { return null; }
    };

    const tryFlexMatch = (needle) => {
      if (!needle?.trim()) return null;
      // 1a. Exact indexOf (fast path)
      const idx = text.indexOf(needle.trim());
      if (idx !== -1) return { start: idx, end: idx + needle.trim().length };
      // 1b. Case-insensitive indexOf
      const lower = text.toLowerCase();
      const idx2  = lower.indexOf(needle.trim().toLowerCase());
      if (idx2 !== -1) return { start: idx2, end: idx2 + needle.trim().length };
      // 1c. Flexible-whitespace regex (handles OCR newlines)
      const rx = makeFlexRegex(needle);
      if (rx) {
        const m = rx.exec(text);
        if (m) return { start: m.index, end: m.index + m[0].length };
      }
      return null;
    };

    // Strategy 1: full context.match
    const match = ev.context?.match;
    const r1 = tryFlexMatch(match);
    if (r1) return [r1];

    // Strategy 2: first sentence / clause of context.match
    //   e.g. "Platelets decreased (HCC) Not at goal; repeat" → "Platelets decreased (HCC)"
    if (match) {
      const firstClause = match.split(/[;,\n]/)[0].trim();
      if (firstClause && firstClause !== match.trim()) {
        const r2 = tryFlexMatch(firstClause);
        if (r2) return [r2];
      }
    }

    // Strategy 3a: entity_text (coded_findings.evidence field)
    const r3 = tryFlexMatch(ev.entity_text);
    if (r3) return [r3];

    // Strategy 3b: text field (corroboration.supporting_evidence field e.g. "INR: 2.7")
    const r3b = tryFlexMatch(ev.text);
    if (r3b) return [r3b];

    // Strategy 4: char_span — last resort, may be offset from full-chart text
    const cs = ev.char_span;
    if (cs?.length === 2) return [{ start: cs[0], end: cs[1] }];
    return [];
  };

  const evidenceSpans = getHighlightSpans(ocrText, currentSupport);

  return (
    <Layout ref={telemetryRootRef} style={{ minHeight: '100vh', background: C.bg }}>

      {/* Inject audit-pulse keyframes (inline styles can't define @keyframes) */}
      <style>{`
        @keyframes audit-pulse {
          0%, 100% { opacity: 1; }
          50%      { opacity: 0.7; }
        }
      `}</style>

      {/* ── Header ── */}
      <Header style={{
        background: C.navy, padding: '0 20px', height: 56,
        display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', flexShrink: 0,
        position: 'sticky', top: 0, zIndex: 200,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span
            onClick={() => navigate('/hcc-home')}
            style={{
              color: 'rgba(255,255,255,0.65)', fontSize: 19, fontWeight: 700,
              cursor: 'pointer', whiteSpace: 'nowrap',
            }}
            onMouseEnter={e => e.currentTarget.style.color = 'white'}
            onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.65)'}
          >
            HCC WORKSPACE
          </span>
          <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: 19 }}>/</span>
          <span
            onClick={() => navigate('/my-inventory/charts')}
            style={{
              color: 'rgba(255,255,255,0.65)', fontSize: 19, fontWeight: 700,
              cursor: 'pointer', whiteSpace: 'nowrap',
            }}
            onMouseEnter={e => e.currentTarget.style.color = 'white'}
            onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.65)'}
          >
            My Inventory
          </span>
          <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: 19 }}>/</span>
          <span style={{ color: 'white', fontSize: 19, fontWeight: 700, fontFamily: 'monospace' }}>
            {chartId}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>

        <CodingGuidelinesButton selectedIcdCode={selectedFinding?.icd10_code} />

        {!viewOnly && (
          <Button
            icon={<SaveOutlined />}
            onClick={handleSaveDraft}
            data-telemetry-label="save_draft"
            style={{ background: 'white', color: C.navy, fontWeight: 600, border: 'none', fontSize: 16 }}
          >
            Save Draft
          </Button>
        )}
          
        {/*  
        <Button
          icon={<CheckCircleOutlined />}
          onClick={handleComplete}
          style={{ background: C.green, color: 'white', fontWeight: 600, border: 'none', fontSize: 16 }}
        >
          Complete
        </Button>
        */}

        </div>
      </Header>

      {/* ── Action Bar — column labels + decision buttons ── */}
      {(() => {
        const currentDec = decisions[selectedFinding?.finding_id]?.decision || 'pending';
        const isActive = currentDec !== 'pending' && currentDec !== 'reverted' && currentDec !== 'reset' && currentDec !== 'recalled';
        const actionBtnStyle = (color, isCurrentAction) => ({
          padding: '5px 16px', fontSize: 13, fontWeight: 700,
          borderRadius: 4, cursor: selectedFinding ? 'pointer' : 'not-allowed',
          border: isCurrentAction ? 'none' : `2px solid ${color}`,
          background: isCurrentAction ? color : 'white',
          color: isCurrentAction ? 'white' : color,
          opacity: selectedFinding ? 1 : 0.4,
          display: 'flex', alignItems: 'center', gap: 5,
          transition: 'all 0.15s',
          letterSpacing: '0.03em',
        });
        return (
          <div style={{
            display: 'flex', alignItems: 'center',
            background: '#6e7988', padding: '0 0 0 0',
            borderBottom: `2px solid ${C.navy}`,
            flexShrink: 0, minHeight: 42,
          }}>

            {/* Left: ENCOUNTERS label — aligned with P1 */}
            <div style={{
              width: W.p1, flexShrink: 0,
              padding: '0 16px',
              display: 'flex', alignItems: 'center',
            }}>
              <span style={{
                fontSize: 14, fontWeight: 700, letterSpacing: '0.12em',
                textTransform: 'uppercase', color: '#FFFFFF',
              }}>
                Encounters
              </span>
            </div>
            {/* PROPOSED CODES label — aligned with P2 */}
            <div style={{
              width: W.p2, flexShrink: 0,
              padding: '0 16px',
              display: 'flex', alignItems: 'center',
              borderLeft: '1px solid rgba(255,255,255,0.1)',
            }}>
              <span style={{
                fontSize: 14, fontWeight: 700, letterSpacing: '0.12em',
                textTransform: 'uppercase', color: '#FFFFFF',
              }}>
                Proposed Codes
              </span>
            </div>



            {/* Center: CODE EVIDENCE label + action buttons — spans P3 */}
            <div style={{
              flex: 5, display: 'flex', alignItems: 'center',
              padding: '0 16px', gap: 12,
              borderLeft: '1px solid rgba(255,255,255,0.1)',
              borderRight: '1px solid rgba(255,255,255,0.1)',
            }}>
              <span style={{
                fontSize: 14, fontWeight: 700, letterSpacing: '0.12em',
                textTransform: 'uppercase', color: '#FFFFFF',
                marginRight: 'auto',
              }}>
                Code Evidence
              </span>
              {selectedFinding && (historyCounts[selectedFinding.finding_id] || 0) > 0 && (
                <Tooltip title="View decision history">
                  <span style={{ position: 'relative', display: 'inline-flex', cursor: 'pointer' }}
                        onClick={() => fetchDecisionHistory(selectedFinding)}
                        data-telemetry-label="open_decision_history" data-telemetry-finding-id={selectedFinding?.finding_id}>
                    <InfoCircleOutlined style={{ fontSize: 16, color: '#FFFFFF' }} />
                    <span style={{
                      position: 'absolute', top: -6, right: -8,
                      fontSize: 9, fontWeight: 700, color: 'white',
                      background: '#EF4444', borderRadius: '50%',
                      width: 14, height: 14, display: 'flex',
                      alignItems: 'center', justifyContent: 'center',
                    }}>
                      {historyCounts[selectedFinding.finding_id]}
                    </span>
                  </span>
                </Tooltip>
              )}


              {/* Action buttons — only show when a finding is selected */}
              {selectedFinding && !isActive && !viewOnly && (
                <div style={{ display: 'flex', gap: 8 }}>
                  <button style={actionBtnStyle('#15803D', false)} onClick={() => handleAccept(selectedFinding)}
                    data-telemetry-label="accept_finding" data-telemetry-finding-id={selectedFinding?.finding_id}>
                    <CheckOutlined style={{ fontSize: 12 }} /> ACCEPT
                  </button>
                  <button style={actionBtnStyle('#1D4ED8', false)} onClick={() => handleModify(selectedFinding)}
                    data-telemetry-label="modify_finding" data-telemetry-finding-id={selectedFinding?.finding_id}>
                    <EditOutlined style={{ fontSize: 12 }} /> MODIFY
                  </button>
                  <button style={actionBtnStyle('#B45309', false)} onClick={() => handleEscalate(selectedFinding)}
                    data-telemetry-label="escalate_finding" data-telemetry-finding-id={selectedFinding?.finding_id}>
                    <ExclamationCircleOutlined style={{ fontSize: 12 }} /> REVIEW
                  </button>
                  <button style={actionBtnStyle('#DC2626', false)} onClick={() => handleReject(selectedFinding)}
                    data-telemetry-label="reject_finding" data-telemetry-finding-id={selectedFinding?.finding_id}>
                    <StopOutlined style={{ fontSize: 12 }} /> REJECT
                  </button>
                </div>
              )}

              {/* Current status + undo — show when a decision is active */}
              {selectedFinding && isActive && !viewOnly && (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  {/* Show ACCEPT button when modified (coder can accept the modified code) */}
                  {(currentDec === 'modified' || currentDec === 'under_review') && (
                    <button style={actionBtnStyle('#15803D', false)} onClick={() => handleAccept(selectedFinding)}
                      data-telemetry-label="accept_finding" data-telemetry-finding-id={selectedFinding?.finding_id}>
                      <CheckOutlined style={{ fontSize: 12 }} /> ACCEPT
                    </button>
                  )}
                  <span style={{
                    ...actionBtnStyle(REVIEW_STATUS[currentDec]?.color || '#94A3B8', true),
                    cursor: 'default',
                  }}>
                    {currentDec === 'accepted'     && <><CheckOutlined style={{ fontSize: 12 }} /> ACCEPTED</>}
                    {currentDec === 'modified'      && <><EditOutlined style={{ fontSize: 12 }} /> MODIFIED</>}
                    {currentDec === 'under_review'  && <><ExclamationCircleOutlined style={{ fontSize: 12 }} /> IN REVIEW</>}
                    {currentDec === 'rejected'      && <><StopOutlined style={{ fontSize: 12 }} /> REJECTED</>}
                  </span>
                  <button
                    onClick={() => handleRevertDecision(selectedFinding)}
                    data-telemetry-label="revert_decision" data-telemetry-finding-id={selectedFinding?.finding_id}
                    style={{
                      padding: '5px 12px', fontSize: 13, fontWeight: 600,
                      borderRadius: 4, cursor: 'pointer',
                      border: '1px solid rgba(255,255,255,0.3)',
                      background: 'transparent', color: '#FFFFFF',
                      display: 'flex', alignItems: 'center', gap: 4,
                    }}
                  >
                    <UndoOutlined style={{ fontSize: 12 }} />
                    {currentDec === 'modified' ? 'Reset' : currentDec === 'under_review' ? 'Recall' : 'Revert'}
                  </button>
                </div>
              )}
            </div>

            {/* Right: CODE SUPPORT label — spans P4 */}
            <div style={{
              flex: 4, padding: '0 16px',
              display: 'flex', alignItems: 'center',
            }}>
              <span style={{
                fontSize: 14, fontWeight: 700, letterSpacing: '0.12em',
                textTransform: 'uppercase', color: '#FFFFFF',
              }}>
                Code Support
              </span>
            </div>
          </div>
        );
      })()}

      {/* ── Four panels ── */}
      <div style={{
        display: 'flex',
        height: 'calc(100vh - 98px)', /* 56px header + 42px action bar */
        overflow: 'hidden',
      }}>

        {/* P1 — Encounters */}
        <EncounterList
          encounters={encounters}
          selectedEncId={selectedEnc?.encounter_id}
          onSelect={enc => handleSelectEncounter(enc)}
          loading={encountersLoading}
        />

        {/* P2 — Code list + Add New Code button */}
        <div style={{
          width: W.p2, flexShrink: 0,
          display: 'flex', flexDirection: 'column',
          borderRight: `1px solid ${C.border}`,
        }}>
          <CodeList
            findings={findings}
            selectedFindingId={selectedFinding?.finding_id}
            decisions={decisions}
            onSelectFinding={f => handleSelectFinding(f)}
            loading={traceLoading}
            activeEvidenceIdx={activeCodeEvidenceIdx}
            onEvidenceClick={handleCodeEvidenceClick}
          />
          {/* Add Missed Code button — bottom of P2 */}


          <div style={{
            padding: '10px 12px', flexShrink: 0,
            borderTop: `1px solid ${C.border}`,
            display: 'flex', flexDirection: 'column', gap: 8,
          }}>
            {/* Add Missed Code — existing button */}
            <button
              onClick={() => setHCCAddCodeModalVisible(true)}
              disabled={!selectedEnc || viewOnly}
              data-telemetry-label="open_add_code_modal"
              style={{
                width: '100%', padding: '8px 0',
                background: selectedEnc ? 'white' : '#F9FAFB',
                color: selectedEnc ? C.navy : C.textMuted,
                border: `1.5px dashed ${selectedEnc ? C.navy : C.border}`,
                borderRadius: 5, fontSize: 14, fontWeight: 700,
                cursor: selectedEnc ? 'pointer' : 'not-allowed',
                letterSpacing: '0.03em',
                transition: 'background 0.15s',
              }}
              onMouseEnter={e => { if (selectedEnc) e.currentTarget.style.background = C.navyLight; }}
              onMouseLeave={e => { if (selectedEnc) e.currentTarget.style.background = 'white'; }}
            >
              + Add Missed Code
            </button>
 
            {/* Audit button — only rendered when rules exist */}
            {auditAvailable && (() => {
              const isRunning   = auditPhase === 'running';
              const isCompleted = auditPhase === 'completed';
              const isError     = auditPhase === 'error';
 
              const suspiciousCount = auditFindings.filter(f => f.verdict === 'suspicious').length;
              const errorCount      = auditFindings.filter(f => f.verdict === 'error').length;
              const completedCount  = auditProgress.completed?.length || 0;
              const totalCount      = auditProgress.total || auditSuspects.length;
 
              // Button label
              let label = `Audit Codes (${auditSuspects.length})`;
              if (isRunning) {
                label = `Auditing... ${completedCount}/${totalCount}`;
              } else if (isCompleted) {
                if (errorCount > 0 && errorCount === auditFindings.length) {
                  label = 'Audit Failed — Re-run';
                } else if (errorCount > 0) {
                  label = `Audit Results (${errorCount} error)`;
                } else if (suspiciousCount > 0) {
                  label = `Audit Results (${suspiciousCount} flagged)`;
                } else {
                  label = 'Audit Results ✓';
                }
              }
              if (isError) label = 'Audit Failed — Re-run';
 
              // Button colors
              //   idle / running      → amber (yellow)
              //   errors              → orange
              //   suspicious (no err) → red
              //   all clear           → green
              let btnBg     = '#FEF3C7';   // amber
              let btnBorder = '#F59E0B';
              let btnColor  = '#92400E';
              let hoverBg   = '#FDE68A';
 
              if (isCompleted && errorCount > 0) {
                // Orange for any errors
                btnBg = '#FFF7ED'; btnBorder = '#FB923C'; btnColor = '#9A3412'; hoverBg = '#FFEDD5';
              } else if (isCompleted && suspiciousCount > 0) {
                // Red for suspicious (no errors)
                btnBg = '#FEF2F2'; btnBorder = '#FCA5A5'; btnColor = '#991B1B'; hoverBg = '#FEE2E2';
              } else if (isCompleted) {
                // Green for all clear
                btnBg = '#F0FDF4'; btnBorder = '#86EFAC'; btnColor = '#166534'; hoverBg = '#DCFCE7';
              } else if (isError) {
                // Orange for service-level error
                btnBg = '#FFF7ED'; btnBorder = '#FB923C'; btnColor = '#9A3412'; hoverBg = '#FFEDD5';
              } else {
                btnBg = '#FEF3C7'; btnBorder = '#F59E0B'; btnColor = '#92400E'; hoverBg = '#FEF3C7';
              }
 
              return (
                <Tooltip
                  title={
                    isCompleted
                      ? 'Click to view audit results'
                      : isRunning
                        ? `Evaluating: ${auditProgress.pending?.join(', ') || '...'}`
                        : `${auditSuspects.length} code${auditSuspects.length !== 1 ? 's' : ''} can be audited: ${auditSuspects.join(', ')}`
                  }
                  placement="top"
                >
                  <button
                    onClick={isError ? handleAuditConfirm : handleAuditClick}
                    disabled={isRunning || viewOnly}
                    data-telemetry-label="run_audit" data-telemetry-finding-id={null}
                    style={{
                      width: '100%', padding: '8px 0',
                      background: btnBg,
                      color: btnColor,
                      border: `1.5px solid ${btnBorder}`,
                      borderRadius: 5, fontSize: 14, fontWeight: 700,
                      cursor: isRunning ? 'wait' : 'pointer',
                      letterSpacing: '0.03em',
                      transition: 'background 0.15s',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                      animation: isRunning ? 'audit-pulse 1.5s ease-in-out infinite' : 'none',
                    }}
                    onMouseEnter={e => { if (!isRunning) e.currentTarget.style.background = hoverBg; }}
                    onMouseLeave={e => { if (!isRunning) e.currentTarget.style.background = btnBg; }}
                  >
                    {isRunning && <Spin size="small" />}
                    {label}
                  </button>
                </Tooltip>
              );
            })()}
          </div>
          
        </div>

        {/* P3 — PDF viewer (react-pdf-highlighter-extended, full encounter PDF) */}
        <div style={{
          flex: 5, display: 'flex', flexDirection: 'column',
          borderRight: `1px solid ${C.border}`, background: C.white, minWidth: 0,
        }}>

          {/* ── P3 info strip: encounter range + evidence text ── */}
          <div style={{
            padding: '8px 14px',
            background: '#64748B',
            borderBottom: `1px solid ${C.border}`,
            display: 'flex', alignItems: 'center', gap: 12,
            flexShrink: 0, minHeight: 56,
          }}>
            {/* Left: two-line encounter + evidence info */}
            <div style={{ flex: 1, minWidth: 0 }}>
              {/* Row 1: encounter label */}
              {selectedEnc ? (
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', marginBottom: 2, letterSpacing: '0.02em' }}>
                  ENC {String(selectedEnc.encounter_number).padStart(2, '0')}
                  <span style={{ marginLeft: 8 }}>
                    pp.{selectedEnc.start_page}–{selectedEnc.end_page}
                  </span>
                </div>
              ) : (
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', fontStyle: 'italic' }}>
                  Select an encounter
                </div>
              )}

              {/* Row 2: code + page badge + section badge + evidence quote */}
              {selectedFinding && (() => {
                const activeEv = selectedFinding.evidence?.[activeCodeEvidenceIdx];
                const evText   = activeEv?.context?.match || activeEv?.entity_text || selectedFinding.description;
                return (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <span style={{ fontSize: 16, fontWeight: 700, color: '#FFFFFF', flexShrink: 0 }}>
                      {selectedFinding.icd10_code}
                    </span>
                    {activeEv?.page && (
                      <span style={{
                        fontSize: 11, fontWeight: 700, fontFamily: 'monospace',
                        background: C.navy, color: 'white',
                        padding: '2px 8px', borderRadius: 4, flexShrink: 0,
                      }}>
                        p.{activeEv.page}
                      </span>
                    )}
                    {activeEv?.section && (
                      <span style={{
                        fontSize: 11, fontWeight: 600,
                        background: C.navy, color: 'white',
                        padding: '2px 8px', borderRadius: 4, flexShrink: 0,
                      }}>
                        {activeEv.section}
                      </span>
                    )}
                    {evText && (
                      <span style={{
                        fontSize: 13, color: 'rgba(255,255,255,0.85)',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        minWidth: 0,
                      }}>
                        "{evText.length > 80 ? evText.slice(0, 80) + '…' : evText}"
                      </span>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* Right: star rating */}
            {selectedFinding && (
              <span className="p3-strip-rating">
                <style>{`.p3-strip-rating .ant-rate-star:not(.ant-rate-star-full) .ant-rate-star-second { color: rgba(255,255,255,0.35) !important; }`}</style>
                <Rate
                  defaultValue={0}
                  style={{ fontSize: 20, color: '#FACC15', flexShrink: 0 }}
                />
              </span>
            )}
          </div>

          <P3PdfViewer
            encounterPdfParams={encounterPdfParams}
            startPage={selectedEnc?.start_page ?? 1}
            endPage={selectedEnc?.end_page}
            activePdfPage={activePdfPage}
            evidenceItem={activeCodeEvidenceItem}
            onAddSupport={viewOnly ? undefined : handleAddSupportFromP3}
            findingRef={selectedFindingRef}
          />
        </div>

        {/* P4 — Evidence OCR */}
        <div style={{
          flex: 4, display: 'flex', flexDirection: 'column',
          background: C.white, minWidth: 0, borderLeft: `1px solid ${C.border}`,
        }}>
          <div style={{ ...panelHeadingStyle, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span></span>
            {showSupport && currentSupport?.page && (
              <Tooltip title="View as PDF">
                <button
                  onClick={() => handleViewPdf(currentSupport.page)}
                  style={{
                    background: 'none', border: `1px solid ${C.border}`,
                    borderRadius: 4, cursor: 'pointer', padding: '2px 8px',
                    fontSize: 14, color: C.textMuted, display: 'flex', alignItems: 'center', gap: 3,
                  }}
                >
                  <FilePdfOutlined style={{ fontSize: 14 }} /> View PDF
                </button>
              </Tooltip>
            )}
          </div>

          {/* Support navigator — only when >1 corroboration item */}
          {supportItems.length > 1 && (
            <EvidenceNavigator
              items={supportItems}
              activeIdx={activeEvidenceIdx}
              onNavigate={handleSupportNav}
            />
          )}

          {/* OCR content */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '14px 16px' }}>
            {!selectedFinding ? (
              <div style={{
                height: '100%', display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', gap: 6,
              }}>
                <span style={{ fontSize: 24 }}>📋</span>
                <span style={{ fontSize: 16, color: C.textMuted }}>Select a code to see supporting evidence</span>
              </div>
            ) : selectedFinding?.rule_modifications?.length > 0 && !showSupport ? (
              <RuleDerivationPanel
                finding={selectedFinding}
                encounterTrace={encounterTrace}
                onPageClick={handleViewPdf}
              />
            ) : !showSupport ? (
              <div style={{
                height: '100%', display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', gap: 8, padding: '0 20px',
              }}>
                {isGospelEvidence ? (
                  /* Case 1: Evidence is from a gospel section — fully self-sufficient */
                  <>
                    <span style={{ fontSize: 24 }}>✅</span>
                    <span style={{ fontSize: 16, color: C.textMuted, textAlign: 'center' }}>
                      Found in {(activeEvSection || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())} — no additional corroboration required
                    </span>
                    <div style={{
                      marginTop: 8, background: '#F0F4FF', borderRadius: 6,
                      padding: '8px 12px', textAlign: 'left',
                    }}>
                      <div style={{
                        fontSize: 13, fontWeight: 700, color: C.navy,
                        letterSpacing: '0.08em', marginBottom: 4,
                      }}>REASONING</div>
                      <div style={{ fontSize: 15, color: C.textMuted, fontStyle: 'italic', lineHeight: 1.5 }}>
                        Gospel section: {activeEvSection}
                      </div>
                    </div>
                  </>
                ) : selectedFinding.support_status === 'gospel' ? (
                  /* Case 2: Evidence is from a non-gospel section, but code overall
                     has gospel support (found in A&P / HPI elsewhere). Reassure the coder. */
                  <>
                    <span style={{ fontSize: 24 }}>✅</span>
                    <span style={{ fontSize: 16, color: '#15803D', fontWeight: 600, textAlign: 'center' }}>
                      Support not required
                    </span>
                    <span style={{ fontSize: 14, color: C.textMuted, textAlign: 'center', lineHeight: 1.5 }}>
                      This code is confirmed via a gospel section and does not require additional corroboration.
                    </span>
                    <div style={{
                      marginTop: 6, background: '#FFFBEB', border: '1px solid #FDE68A',
                      borderRadius: 6, padding: '8px 12px', textAlign: 'left', width: '100%',
                    }}>
                      <div style={{
                        fontSize: 13, fontWeight: 700, color: '#92400E',
                        letterSpacing: '0.08em', marginBottom: 4,
                        display: 'flex', alignItems: 'center', gap: 4,
                      }}>
                        <span style={{ fontSize: 16 }}>⚠️</span> NOTE
                      </div>
                      <div style={{ fontSize: 14, color: '#92400E', lineHeight: 1.5 }}>
                        This evidence is from <strong>{(activeEvSection || '').replace(/_/g, ' ')}</strong>, which is not a gospel section.
                        No corroborating evidence was found for this specific reference.
                      </div>
                    </div>
                    {selectedFinding.corroboration?.llm_reasoning && (
                      <div style={{
                        marginTop: 6, background: '#F0F4FF', borderRadius: 6,
                        padding: '8px 12px', textAlign: 'left', width: '100%',
                      }}>
                        <div style={{
                          fontSize: 13, fontWeight: 700, color: C.navy,
                          letterSpacing: '0.08em', marginBottom: 4,
                        }}>REASONING</div>
                        <div style={{ fontSize: 15, color: C.textMuted, fontStyle: 'italic', lineHeight: 1.5 }}>
                          {selectedFinding.corroboration.llm_reasoning}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  /* Case 3: Non-gospel evidence, code genuinely lacks support */
                  <>
                    <span style={{ fontSize: 24 }}>⚠️</span>
                    <span style={{ fontSize: 16, color: C.textMuted, textAlign: 'center' }}>
                      No corroborating evidence found for this code
                    </span>
                    {selectedFinding.corroboration?.llm_reasoning && (
                      <div style={{
                        marginTop: 8, background: '#F0F4FF', borderRadius: 6,
                        padding: '8px 12px', textAlign: 'left',
                      }}>
                        <div style={{
                          fontSize: 13, fontWeight: 700, color: C.navy,
                          letterSpacing: '0.08em', marginBottom: 4,
                        }}>REASONING</div>
                        <div style={{ fontSize: 15, color: C.textMuted, fontStyle: 'italic', lineHeight: 1.5 }}>
                          {selectedFinding.corroboration.llm_reasoning}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            ) : ocrLoading ? (
              <div style={{ textAlign: 'center', paddingTop: 40 }}><Spin /></div>
            ) : (
              <>
                {/* Support match excerpt */}
                <div style={{
                  background: '#FFFBEB', border: `1px solid #FDE68A`,
                  borderRadius: 6, padding: '10px 12px', marginBottom: 14, fontSize: 16,
                }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#92400E', marginBottom: 4, letterSpacing: '0.05em' }}>
                    SUPPORTING EVIDENCE
                    {currentSupport?.evidence_type && (
                      <span style={{
                        marginLeft: 6, background: '#FDE68A', borderRadius: 3,
                        padding: '0 5px', textTransform: 'capitalize',
                      }}>
                        {currentSupport.evidence_type}
                      </span>
                    )}
                  </div>
                  <div style={{ lineHeight: 1.6, color: C.text }}>
                    {currentSupport?.context?.match ? (
                      <>
                        <span style={{ color: C.textMuted }}>{currentSupport.context.before}</span>
                        <mark style={{ background: C.evidenceMark, borderRadius: 3, padding: '0 2px', fontWeight: 700 }}>
                          {currentSupport.context.match}
                        </mark>
                        <span style={{ color: C.textMuted }}>{currentSupport.context.after}</span>
                      </>
                    ) : (
                      <mark style={{ background: C.evidenceMark, borderRadius: 3, padding: '0 2px', fontWeight: 700 }}>
                        {currentSupport?.text}
                      </mark>
                    )}
                  </div>
                  {/* LLM reasoning */}
                  {selectedFinding.corroboration?.llm_reasoning && (
                    <div style={{
                      marginTop: 8, paddingTop: 8,
                      borderTop: `1px solid #FDE68A`,
                    }}>
                      <div style={{
                        fontSize: 13, fontWeight: 700, color: '#92400E',
                        letterSpacing: '0.08em', marginBottom: 3,
                        display: 'flex', alignItems: 'center', gap: 4,
                      }}>
                        REASONING
                      </div>
                      <div style={{ fontSize: 14, color: '#92400E', fontStyle: 'italic', lineHeight: 1.5 }}>
                        {selectedFinding.corroboration.llm_reasoning}
                      </div>
                    </div>
                  )}
                </div>

                {/* Page label */}
                <div style={{
                  fontSize: 14, fontWeight: 700, color: C.textMuted,
                  marginBottom: 10, letterSpacing: '0.08em',
                }}>
                  PAGE {currentSupport?.page} · {currentSupport?.section?.toUpperCase() || '—'}
                </div>

                {/* Full OCR with highlight */}
                <div style={{ fontSize: 16, lineHeight: 1.75 }}>
                  {renderOcrWithHighlights(ocrText, evidenceSpans)}
                </div>
              </>
            )}
          </div>
        </div>

      </div>{/* end four panels */}

      {/* ── Backdrop — closes floating panel when clicking outside ── */}
      {floatingPanel && (
        <div
          onClick={() => setFloatingPanel(null)}
          style={{ position: 'fixed', inset: 0, zIndex: 498, background: 'transparent', cursor: 'default' }}
        />
      )}

      {/* ── Floating buttons ── */}
      <div style={{
        position: 'fixed', bottom: 24, right: 24,
        display: 'flex', flexDirection: 'column', alignItems: 'flex-end',
        gap: 10, zIndex: 500,
      }}>

        {/* Codes accepted popup */}
        {floatingPanel === 'codes' && !viewOnly && (
          <CodesAcceptedPopup
            acceptedFindings={acceptedFindings}
            encounters={encounters}
            onRevoke={handleRevert}
            onFinalize={handleFinalize}
          />
        )}

        {/* Chat popup */}
        {floatingPanel === 'chat' && (
          <ChatPopup
            messages={chatMessages}
            inputValue={chatInput}
            onInputChange={setChatInput}
            onSend={handleChatSend}
            loading={chatLoading}
          />
        )}

        {/* Checkmark button — codes */}
        {!viewOnly && <button
          onClick={() => togglePanel('codes')}
          data-telemetry-label="toggle_codes_popup"
          style={{
            width: 48, height: 48, borderRadius: '50%', border: 'none',
            cursor: 'pointer', position: 'relative',
            background: floatingPanel === 'codes' ? C.navy : C.white,
            boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'background 0.15s',
          }}
        >
          <CheckOutlined style={{
            fontSize: 24,
            color: floatingPanel === 'codes' ? 'white' : C.accepted,
          }} />
          {acceptedFindings.length > 0 && (
            <span style={{
              position: 'absolute', top: -4, right: -4,
              background: C.accepted, color: 'white',
              borderRadius: 9999, fontSize: 14, fontWeight: 700,
              minWidth: 18, height: 18, padding: '0 4px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
            }}>
              {acceptedFindings.length}
            </span>
          )}
        </button>}

        {/* Chat button */}
        <button
          onClick={() => togglePanel('chat')}
          data-telemetry-label="toggle_chat_popup"
          style={{
            width: 48, height: 48, borderRadius: '50%', border: 'none',
            cursor: 'pointer',
            background: floatingPanel === 'chat' ? C.navy : C.white,
            boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'background 0.15s',
          }}
        >
          <MessageOutlined style={{
            fontSize: 24,
            color: floatingPanel === 'chat' ? 'white' : C.navy,
          }} />
        </button>

      </div>

      {/* ── PDF modal with page navigation ── */}
      <Modal
        open={modalVisible}
        onCancel={() => { setModalVisible(false); setModalPdfUrl(null); setModalPage(null); }}
        footer={null}
        width={720}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 18, fontWeight: 700 }}>Page {modalPage} — PDF View</span>
            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <button
                onClick={() => handleViewPdf(modalPage - 1)}
                disabled={!modalPage || modalPage <= (selectedEnc?.start_page ?? 1)}
                style={{
                  width: 26, height: 26, borderRadius: 4, border: `1px solid ${C.border}`,
                  background: 'white', cursor: 'pointer', display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  opacity: (!modalPage || modalPage <= (selectedEnc?.start_page ?? 1)) ? 0.35 : 1,
                }}
              >
                <LeftOutlined style={{ fontSize: 14 }} />
              </button>
              <span style={{ fontSize: 15, color: C.textMuted, minWidth: 80, textAlign: 'center' }}>
                {selectedEnc ? `pp. ${selectedEnc.start_page}–${selectedEnc.end_page}` : ''}
              </span>
              <button
                onClick={() => handleViewPdf(modalPage + 1)}
                disabled={!modalPage || modalPage >= (selectedEnc?.end_page ?? 9999)}
                style={{
                  width: 26, height: 26, borderRadius: 4, border: `1px solid ${C.border}`,
                  background: 'white', cursor: 'pointer', display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  opacity: (!modalPage || modalPage >= (selectedEnc?.end_page ?? 9999)) ? 0.35 : 1,
                }}
              >
                <RightOutlined style={{ fontSize: 14 }} />
              </button>
            </div>
          </div>
        }
        style={{ top: 40 }}
        bodyStyle={{ padding: 0, height: '80vh' }}
      >
        {modalPdfLoading ? (
          <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Spin size="large" />
          </div>
        ) : modalPdfUrl ? (
          <iframe src={modalPdfUrl} style={{ width: '100%', height: '100%', border: 'none' }} title="PDF view" />
        ) : null}
      </Modal>

      {/* ── Add Missed Code modal ── */}
      <HCCAddCodeModal
        visible={addCodeModalVisible}
        onClose={() => setHCCAddCodeModalVisible(false)}
        chartId={chartId}
        sessionId={sessionId}
        encounterInfo={selectedEnc}
        defaultEvidencePage={activePdfPage}
        onCodeAdded={handleCodeAdded}
      />

      <FinalizeReviewModal
        visible={finalizeModalVisible}
        chartId={chartId}
        sessionId={sessionId}
        onClose={() => setFinalizeModalVisible(false)}
        onFinalized={() => {
          setFinalizeModalVisible(false);
          navigate('/my-inventory/charts');
        }}
      />

      <DecisionNoteModal
        visible={decisionModal.visible}
        action={decisionModal.action}
        finding={decisionModal.finding}
        onConfirm={handleDecisionModalConfirm}
        onCancel={() => setDecisionModal({ visible: false, action: null, finding: null })}
      />

      <DecisionHistoryModal
        visible={historyModal.visible}
        onClose={() => setHistoryModal({ visible: false, history: [], loading: false, originalCode: null })}
        history={historyModal.history}
        loading={historyModal.loading}
        originalCode={historyModal.originalCode}
      />

      {/* Audit confirmation modal */}
      <Modal
        open={auditConfirmVisible}
        onCancel={() => setAuditConfirmVisible(false)}
        title="Audit Proposed Codes"
        okText="Start Audit"
        cancelText="Cancel"
        onOk={handleAuditConfirm}
      >
        <p style={{ fontSize: 15, lineHeight: 1.6, marginBottom: 12 }}>
          This will audit <strong>{auditSuspects.length} code{auditSuspects.length !== 1 ? 's' : ''}</strong> against
          FP detection rules using LLM analysis.
        </p>
        <p style={{ fontSize: 14, color: C.textMuted, lineHeight: 1.5, marginBottom: 12 }}>
          Codes to audit: <strong>{auditSuspects.join(', ')}</strong>
        </p>
        <p style={{ fontSize: 14, color: C.textMuted, lineHeight: 1.5 }}>
          The scan runs in the background — you can continue reviewing
          while it processes. This typically takes 15–30 seconds.
        </p>
      </Modal>

      {/* Audit results popup */}
      <AuditResultsPopup
        visible={auditPopupVisible}
        onClose={() => setAuditPopupVisible(false)}
        findings={auditFindings}
        auditMeta={auditMeta}
        onRerun={handleAuditRerun}
      />

    </Layout>
  );
};

export default HCCReviewPage;